import { format } from 'date-fns';
import type PikadayType from 'pikaday';
import type { PikadayOptions } from 'pikaday';
import { useEffect, useRef } from 'react';

declare let Pikaday: typeof PikadayType;

const ISO_DATE_FORMAT_PATTERN = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;

const pikadayOptions: PikadayOptions = {
    firstDay: 1,
    toString: (date) => {
        return format(date, 'yyyy-MM-dd');
    },
    parse: (dateString) => {
        return new Date(dateString);
    },
    i18n: {
        previousMonth: 'Förra månaden',
        nextMonth: 'Nästa månad',
        months: ['Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni', 'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December'],
        weekdays: ['Söndag', 'Måndag', 'Tisdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lördag'],
        weekdaysShort: ['Sön', 'Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör'],
    },
};

const formatDate = (date?: Date) => {
    return date ? format(date, 'yyyy-MM-dd') : '';
};

export interface DatePickerProps {
    id?: string;
    name?: string;
    disabled?: boolean;
    className?: string;
    maxDate?: Date;
    minDate?: Date;
    onChange?: (date: string) => void;
    placeholder?: string;
    value?: string;
}

export const DatePicker = (props: DatePickerProps) => {
    const ref = useRef<HTMLInputElement>(null);
    const picker = useRef<PikadayType>(null);

    useEffect(() => {
        if (ref.current) {
            picker.current = new Pikaday({
                ...pikadayOptions,
                field: ref.current,
            });
        }

        return () => {
            if (picker.current) {
                picker.current.destroy();
            }
        };
    }, []);

    useEffect(() => {
        if (picker.current && formatDate(picker.current.getDate()) !== props.value) {
            picker.current.setDate(props.value, true);
        }
    }, [props.value]);

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;
        const pickerValue = picker.current?.getDate();
        if (inputValue && (!pickerValue || !inputValue.match(ISO_DATE_FORMAT_PATTERN))) {
            // Reset input to last picked date
            ref.current.value = formatDate(pickerValue);
        } else if (props.onChange && inputValue !== props.value) {
            props.onChange(inputValue);
        }
    };

    return (
        <input
            ref={ref}
            className={props.className || ''}
            id={props.id}
            name={props.name}
            type="text"
            placeholder="ÅÅÅÅ-MM-DD"
            autoComplete="off"
            defaultValue={props.value}
            onBlur={handleBlur}
            disabled={props.disabled}
        />
    );
};
